import { brandsOpenApi } from '@constants';
import {
    CAMPAIGN_PRODUCTS_LIST_TYPES,
    CAMPAIGN_STATUS,
    CAMPAIGN_SUBMIT_ERROR,
    CAMPAIGN_SYNC_STATUS,
    CAMPAIGN_TYPE_ID
} from '@enums';
import { axiosBoa } from '@utils';
import { notification } from 'antd';
import { getParent, types } from 'mobx-state-tree';
import { models } from './constants';
import { createStoreAction } from './utils';

export const CampaignsStore = types
    .model('CampaignsStore', {
        campaigns: types.array(models.CampaignModel),
        limitedCampaigns: types.optional(types.array(models.CampaignModel), []),
        meta: types.optional(
            types.model({
                page: types.number,
                perPage: types.number,
                total: types.number,
                totalPages: types.number
            }),
            {
                page: 1,
                perPage: 10,
                total: 0,
                totalPages: 0
            }
        ),
        filters: types.model({
            catalog_types: types.maybe(types.array(types.number)),
            statuses: types.maybe(types.array(types.number)),
            search: types.maybe(types.string)
        }),
        sorters: types.model({
            sort: types.maybe(types.string),
            order: types.maybe(types.string)
        }),
        pending: false,
        campaignCommissions: types.array(types.number),
        hasAnyCampaign: false
    })
    .views((self) => ({
        get root() {
            return getParent(self);
        },
        getCampaign(id) {
            return self.campaigns.find((campaign) => campaign.id === id);
        },
        getStoreCampaignProducts(campaignIndex) {
            return self.campaigns[campaignIndex]?.products?.data || [];
        },
        get hasPreLaunchedCampaign() {
            return self.campaigns.some((campaign) => campaign.status === CAMPAIGN_STATUS.PRE_LAUNCHED);
        }
    }))
    .actions((self) => {
        const action = createStoreAction(self);

        return {
            resetFilters() {
                self.filters = {};
            },
            updateFilters(filters) {
                Object.assign(self.filters, filters);
            },
            resetSorters() {
                self.sorters = {};
            },
            updateSorters(sorters) {
                Object.assign(self.sorters, sorters);
            },
            getLimitedCampaigns: action(function* getLimitedCampaigns() {
                const params = {};
                Object.assign(params, {
                    catalog_types: [
                        CAMPAIGN_TYPE_ID.AI_RECOMMENDED,
                        CAMPAIGN_TYPE_ID.MY_ENTIRE_CATALOG,
                        CAMPAIGN_TYPE_ID.BEST_SELLERS
                    ]
                });

                const data = yield axiosBoa.get(brandsOpenApi.campaigns.list, { params });
                self.limitedCampaigns = data.data;
            }),
            getCampaigns: action(
                function* getCampaigns(p = {}) {
                    self.pending = true;
                    const params = {};
                    Object.assign(params, self.filters, self.sorters, p);

                    const data = yield axiosBoa.get(brandsOpenApi.campaigns.list, { params });
                    self.campaigns = data.data;
                    self.meta = data.meta;
                    yield self.root.googleAdsStore.getData();
                },
                { onFinally: () => (self.pending = false) }
            ),
            checkHasAnyCampaign: action(function* checkHasAnyCampaign() {
                const response = yield axiosBoa.get(brandsOpenApi.campaigns.list, { params: { limit: 1 } });
                self.hasAnyCampaign = !!response.data.length;
            }),
            getCampaignProducts: action(
                function* getCampaignProducts(campaignId, page = 1, limit = 10) {
                    const campaign = self.getCampaign(campaignId);
                    campaign.productsPending = true;

                    const data = yield axiosBoa.post(brandsOpenApi.campaigns.products.listPost(campaignId), {
                        type: CAMPAIGN_PRODUCTS_LIST_TYPES.INCLUDED,
                        page,
                        limit
                    });
                    campaign.products = data;
                },
                {
                    onFinally: (campaignId) => {
                        const campaign = self.getCampaign(campaignId);
                        campaign.productsPending = false;
                    }
                }
            ),
            getCampaignCommissions: action(function* getCampaignCommissions() {
                const response = yield axiosBoa.get(brandsOpenApi.campaigns.list, {
                    params: {
                        select: 'campaign_commissions',
                        pagination: 'none',
                        fields: ['commission']
                    }
                });
                const uniqueCommissions = [...new Set(response.data.map((item) => item.commission))];
                self.campaignCommissions = uniqueCommissions;
            }),
            pauseCampaign: action(
                function* pauseCampaign(campaignId) {
                    const campaign = self.getCampaign(campaignId);
                    campaign.pending = true;

                    const pausedCampaign = yield axiosBoa.patch(brandsOpenApi.campaigns.patch(campaignId), {
                        status: CAMPAIGN_STATUS.PAUSED
                    });
                    Object.assign(campaign, pausedCampaign);
                },
                {
                    onFinally: (campaignId) => {
                        setTimeout(() => {
                            self.setPendingOff(campaignId);
                        }, 5000);
                    }
                }
            ),
            resumeCampaign: action(
                function* resumeCampaign(campaignId) {
                    const campaign = self.getCampaign(campaignId);
                    campaign.pending = true;

                    const { ready, notConfirmed } = yield axiosBoa.post(brandsOpenApi.campaigns.submit(campaignId));
                    if (ready) {
                        notification.success({
                            message: 'Success',
                            description: (
                                // eslint-disable-next-line react/react-in-jsx-scope
                                <span data-e2e='campaigns_resumed-successfully_notification'>
                                    Campaign will be active soon
                                </span>
                            )
                        });
                    } else {
                        Object.values(notConfirmed).map((error) => {
                            notification.error({
                                message: 'Can not activate campaign',
                                description: (
                                    // eslint-disable-next-line react/react-in-jsx-scope
                                    <span data-e2e='campaigns_cannot-resume_notification'>
                                        {CAMPAIGN_SUBMIT_ERROR[error.name]}
                                    </span>
                                ),
                                duration: 0
                            });
                        });
                    }
                },
                {
                    onFinally: (campaignId) => {
                        self.reFetchCampaign(campaignId, [
                            CAMPAIGN_STATUS.ACTIVE,
                            CAMPAIGN_STATUS.PAUSED,
                            CAMPAIGN_STATUS.CREATED,
                            CAMPAIGN_STATUS.DELETED,
                            CAMPAIGN_STATUS.FROZEN,
                            CAMPAIGN_STATUS.PRE_LAUNCHED
                        ]);
                    }
                }
            ),
            deleteCampaign: action(
                function* deleteCampaign(campaignId) {
                    const campaign = self.getCampaign(campaignId);
                    campaign.isDeleting = true;

                    const deletedCampaign = yield axiosBoa.delete(brandsOpenApi.campaigns.patch(campaignId));
                    Object.assign(campaign, deletedCampaign);
                },
                {
                    onFinally: (campaignId) => {
                        const campaign = self.getCampaign(campaignId);
                        campaign.isDeleting = false;
                    }
                }
            ),
            setPendingOff(campaignId) {
                const campaign = self.getCampaign(campaignId);
                campaign.pending = false;
            },
            reFetchCampaign: action(
                function* refetchCampaign(campaignId, expectedStatuses) {
                    const campaign = self.getCampaign(campaignId);

                    const reFetchedCampaign = yield axiosBoa.get(brandsOpenApi.campaigns.get(campaignId));
                    Object.assign(campaign, reFetchedCampaign);
                },
                {
                    onFinally: (campaignId, expectedStatuses) => {
                        const campaign = self.getCampaign(campaignId);

                        if (
                            expectedStatuses.includes(campaign.status) &&
                            campaign.syncStatus !== CAMPAIGN_SYNC_STATUS.IN_PROGRESS
                        ) {
                            campaign.pending = false;
                        } else {
                            setTimeout(() => {
                                self.reFetchCampaign(campaignId, expectedStatuses);
                            }, 3000);
                        }
                    }
                }
            )
        };
    });
